import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import { useMemo, useRef } from "react";
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";

// Once more than this many values are selected, the selected chips switch
// from the default inline-wrap layout to a full-width stacked list — easier
// to scan and delete individually. Threshold of 1 → stacks at 2+ selections.
const STACK_THRESHOLD = 1;

const UNCHECKED_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKED_ICON = <CheckBoxIcon fontSize="small" />;

const SelectMultipleCheckboxInput = ({
  value = [],
  onChange,
  fullWidth = true,
  sx,
  options,
  size = "small",
  onBlur,
  label,
  chipMaxWidth = "unset",
  placeholder,
  ChipProps,
  disableStacking = false,
  ...rest
}) => {
  const inputRef = useRef(null);

  // Upstream data isn't always an array — some fields (e.g. mortgage_lates,
  // housing_events) arrive as a scalar string/number. Coerce to an array so
  // `.includes` and `.length` below never blow up on a non-array value.
  const valueArray = useMemo(
    () => (Array.isArray(value) ? value : isNil(value) ? [] : [value]),
    [value],
  );

  const selectedOptions = useMemo(() => {
    return options.filter((option) =>
      isString(option)
        ? valueArray.includes(option)
        : valueArray.includes(option.value),
    );
  }, [valueArray, options]);

  // Stack the chips one-per-row once the selection grows past the threshold.
  // Each chip is still sized to its content width — not stretched full-width.
  // `disableStacking` opts callers out of this behavior — chips stay inline
  // and wrap to the next row as needed (used by PanelRow inline editors,
  // where the surrounding row already provides constrained horizontal space).
  const stacked = !disableStacking && valueArray.length > STACK_THRESHOLD;

  const styles = useMemo(() => {
    return {
      ".MuiInputBase-root": {
        gap: "5px",
        // When stacked, lay the chips out in a column so each takes its own
        // row, sized to its content; the text input flows below them.
        ...(stacked
          ? { flexDirection: "column", alignItems: "flex-start" }
          : {}),
      },
      ".MuiAutocomplete-endAdornment": { marginRight: "5px" },
      ".MuiChip-root": {
        marginTop: 0,
        marginBottom: 0,
        ".MuiChip-label": {
          fontSize: "11px",
          fontWeight: 400,
          maxWidth: stacked ? "unset" : chipMaxWidth,
          paddingLeft: "8px",
          paddingRight: "8px",
          color: "#33475b",
        },
      },
      ...sx,
    };
  }, [sx, stacked, chipMaxWidth]);

  const emptyPlacholderValue = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }
    return "Start Typing...";
  }, [placeholder]);

  const isSelected = (option) => {
    const optionValue = isString(option) ? option : option.value;
    return valueArray.includes(optionValue);
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      onBlur={onBlur}
      value={selectedOptions}
      fullWidth={fullWidth}
      size={size}
      options={options}
      sx={styles}
      onChange={(event, newValue) => {
        const selectedValues = newValue.map((option) =>
          isString(option) ? option : option.value,
        );
        onChange(selectedValues);

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }}
      isOptionEqualToValue={(option, val) => {
        const optVal = isString(option) ? option : option.value;
        const valVal = isString(val) ? val : val.value;
        return optVal === valVal;
      }}
      getOptionLabel={(option) => (isString(option) ? option : option.label)}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const selected = isSelected(option);
        const label = isString(option) ? option : option.label;
        const keyValue = isString(option) ? option : option.value;

        return (
          <Box
            key={keyValue}
            component="li"
            {...optionProps}
            sx={{
              fontSize: ".8rem",
              display: "flex",
              alignItems: "center",
              fontWeight: selected ? 600 : 400,
              backgroundColor: selected
                ? "rgba(25, 118, 210, 0.08)"
                : "transparent",
              "&:hover": {
                backgroundColor: selected
                  ? "rgba(25, 118, 210, 0.12) !important"
                  : undefined,
              },
            }}
          >
            <Checkbox
              size="small"
              icon={UNCHECKED_ICON}
              checkedIcon={CHECKED_ICON}
              checked={selected}
              sx={{ mr: 1, p: 0.25 }}
            />
            {label}
          </Box>
        );
      }}
      ChipProps={ChipProps}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const chipLabel = isString(option) ? option : option.label;
          return (
            <Chip
              {...tagProps}
              key={key}
              size="small"
              label={chipLabel}
              sx={
                stacked
                  ? {
                      // One chip per row (column layout on the input root),
                      // sized to its own content — not stretched full-width.
                      alignSelf: "flex-start",
                      maxWidth: "100%",
                      height: "auto",
                      minHeight: 28,
                      m: "1px 0 !important",
                      borderRadius: "6px",
                      ".MuiChip-label": {
                        whiteSpace: "normal",
                        py: "3px",
                      },
                    }
                  : { maxWidth: chipMaxWidth }
              }
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth={fullWidth}
          placeholder={emptyPlacholderValue}
          inputRef={inputRef}
          endAdornment={params.InputProps.endAdornment}
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  );
};

export default SelectMultipleCheckboxInput;
